import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, AwaitModules, Button, ComponentSync, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend';
import './Component_AdvisorNotes.scss';
import { ModuleFE_Advisor_NoteTemplate, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { exists, filterInstances } from '@nu-art/ts-common';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { dispositionType_Admit, dispositionType_Discharge } from '@app/advisor-v5-shared';
import { Component_AdvisorNoteOption } from '@app/advisor-content-frontend/ui/components/advisor-note-option/Component_AdvisorNoteOption';
import { getDispoOptions } from '../../../../_utils/get-dispo-options.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../_analytics/consts.js';
import { ModuleFE_Notes } from '../../../../modules/ModuleFE_Notes/ModuleFE_Notes.js';
import { NotesRenderKey } from '../../../../modules/ModuleFE_Notes/types.js';
import { ProtocolData } from '../../../../_utils/ProtocolData/ProtocolData.js';
import { Component_AdvisorSL_Notes } from '../../../loaders/skeleton/notes/Component_AdvisorSL_Notes.js';
import { formatNoteAIText, stripDispositionSections } from './utils.js';
class Component extends ComponentSync {
    static modules = [ModuleFE_Advisor_NoteTemplate, ...ProtocolData.modulesToAwait];
    //######################### Life Cycle #########################
    __onRuntimeProtocolsUpdated = (...params) => this.forceUpdate();
    __onNotesRenderKeyChange = () => this.forceUpdate();
    deriveStateFromProps(nextProps, state) {
        const noteIds = ModuleFE_Notes.allowedTemplateIds;
        state.notes ??= filterInstances(noteIds.map(id => ModuleFE_Advisor_NoteTemplate.cache.unique(id)));
        return state;
    }
    componentDidMount() {
        ModuleFE_AdvisorAnalytics.trigger.uiLoad.uiLoad_liveMdm({
            elapsed_ms: ModuleFE_AdvisorAnalytics.getTimeSinceLoad(),
        });
    }
    //######################### Logic #########################
    getFooterActions = () => {
        const selectedNote = ModuleFE_Advisor_NoteTemplate.cache.unique(ModuleFE_Notes.selectedTemplateId);
        switch (ModuleFE_Notes.renderKey) {
            case NotesRenderKey.Notes: {
                return [
                    {
                        key: 'add-dispo',
                        label: 'Add Disposition',
                        disabled: !selectedNote?.advisorAppearance?.dispositionable,
                        variant: 'secondary',
                        action: () => {
                            ModuleFE_Notes.renderKey = NotesRenderKey.Protocols;
                            const dispoOptions = getDispoOptions();
                            if (dispoOptions.length > 1) {
                                const pathway = dispoOptions[0].data?.pathway;
                                ModuleFE_Notes.setDiagnosis(pathway?._id, pathway?.label);
                            }
                            ModuleFE_Notes.analytics.protocolSelection.open();
                        },
                    },
                    {
                        key: 'send',
                        label: 'Create Note',
                        disabled: !selectedNote,
                        variant: 'secondary',
                        action: async () => {
                            const response = await ModuleFE_Notes.generateStampedNote(selectedNote._id, 'manual');
                            if (response.status === 'fail')
                                return;
                            ModuleFE_Notes.analytics.note.open();
                        }
                    }
                ];
            }
            case NotesRenderKey.Protocols: {
                return [
                    {
                        key: 'cancel',
                        label: 'Cancel',
                        variant: 'secondary',
                        disabled: false,
                        action: () => {
                            ModuleFE_Notes.analytics.protocolSelection.abandon();
                            ModuleFE_Notes.setDiagnosis(undefined, undefined);
                            ModuleFE_Notes.dispositionType = undefined;
                            ModuleFE_Notes.renderKey = NotesRenderKey.Notes;
                        },
                    },
                    {
                        key: 'discharge',
                        label: 'Discharge Note',
                        variant: 'primary',
                        disabled: false,
                        action: async () => {
                            if (!selectedNote)
                                return;
                            ModuleFE_Notes.dispositionType = dispositionType_Discharge;
                            ModuleFE_Notes.analytics.protocolSelection.submit();
                            const protocol = ModuleFE_RuntimeProtocol.cache.find(i => i.pathwayId === ModuleFE_Notes.diagnosisId);
                            const data = ProtocolData.createNew(protocol?._id);
                            const response = await ModuleFE_Notes.generateStampedNote(selectedNote._id, 'manual', data, dispositionType_Discharge);
                            if (response.status === 'fail')
                                return;
                            ModuleFE_Notes.analytics.note.open();
                        },
                    },
                    {
                        key: 'admit',
                        label: 'Admission Note',
                        variant: 'primary',
                        disabled: false,
                        action: async () => {
                            if (!selectedNote)
                                return;
                            ModuleFE_Notes.dispositionType = dispositionType_Admit;
                            ModuleFE_Notes.analytics.protocolSelection.submit();
                            const protocol = ModuleFE_RuntimeProtocol.cache.find(i => i.pathwayId === ModuleFE_Notes.diagnosisId);
                            const data = ProtocolData.createNew(protocol?._id);
                            const response = await ModuleFE_Notes.generateStampedNote(selectedNote._id, 'manual', data, dispositionType_Admit);
                            if (response.status === 'fail')
                                return;
                            ModuleFE_Notes.analytics.note.open();
                        },
                    },
                ];
            }
            case NotesRenderKey.Review: {
                return [
                    {
                        key: 'cancel',
                        label: 'Cancel',
                        disabled: false,
                        variant: 'secondary',
                        action: () => {
                            ModuleFE_Notes.analytics.note.abandon();
                            ModuleFE_Notes.clearStampedNote();
                        },
                    },
                    {
                        key: 'send',
                        label: 'Send To EHR',
                        variant: 'primary',
                        disabled: false,
                        action: async () => {
                            const sent = await ModuleFE_Notes.sendToEHR();
                            if (sent)
                                ModuleFE_Notes.analytics.note.sent();
                        },
                    }
                ];
            }
            case NotesRenderKey.Sent: {
                return [{
                        key: 'clear',
                        label: 'Return to Patient Summary',
                        disabled: false,
                        variant: 'secondary',
                        action: () => {
                            ModuleFE_Notes.clearStampedNote();
                            ModuleFE_Notes.closeNotesPanel();
                        },
                    }];
            }
            default:
                return [];
        }
    };
    removeDisposition = () => {
        const stampedNote = ModuleFE_Notes.stampedNote;
        if (!stampedNote)
            return;
        ModuleFE_Notes.removeStampedNoteDisposition(stripDispositionSections(stampedNote.note.aiText));
        this.forceUpdate();
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { className: 'c__advisor-notes', children: [this.render_Main(), this.render_FooterActions()] });
    }
    render_Main = () => {
        switch (ModuleFE_Notes.renderKey) {
            case NotesRenderKey.Notes:
                return this.render_NoteSelection();
            case NotesRenderKey.Protocols:
                return this.render_ProtocolSelection();
            case NotesRenderKey.Review:
                return this.render_Review();
            case NotesRenderKey.Sent:
                return this.render_Sent();
            default:
                return _jsx(_Fragment, {});
        }
    };
    render_FooterActions = () => {
        const footerActions = this.getFooterActions();
        return _jsx(LL_H_C, { className: 'c__advisor-notes__footer-actions', children: footerActions.map(action => {
                return _jsx(Button, { onClick: action.action, variant: action.variant, disabled: action.disabled, children: action.label }, action.key);
            }) });
    };
    //######################### Render - Main #########################
    render_NoteSelection = () => {
        return _jsx(FadeScrollWrapper, { className: 'c__advisor-notes__notes', children: this.state.notes.map(note => {
                const selected = note._id === ModuleFE_Notes.selectedTemplateId;
                return _jsx(Component_AdvisorNoteOption, { note: note, selected: selected, onSelect: note => {
                        ModuleFE_Notes.selectedTemplateId = note._id;
                        ModuleFE_Notes.analytics.note.select();
                        this.forceUpdate();
                    } }, note._id);
            }) });
    };
    render_ProtocolSelection = () => {
        const dispoOptions = getDispoOptions();
        return _jsx(FadeScrollWrapper, { className: 'c__advisor-notes__dispo-options', children: dispoOptions.map(option => {
                const selected = option.data?.pathway._id === ModuleFE_Notes.diagnosisId;
                const color = option.data?.getColor();
                const style = {};
                if (color)
                    style['--p-color'] = color;
                return _jsxs(LL_H_C, { className: _className('c__advisor-notes__dispo-option', selected && 'selected', !option.data && 'other'), style: style, onClick: () => {
                        if (selected)
                            return;
                        ModuleFE_Notes.setDiagnosis(option.data?.pathway._id, option.data?.pathway.label);
                        ModuleFE_Notes.analytics.protocolSelection.select();
                        this.forceUpdate();
                    }, children: [_jsx("span", { className: 'c__advisor-notes__dispo-option-label', children: option.label }), _jsx("div", { className: 'c__advisor-notes__dispo-option-check' })] }, option.data?.pathway._id ?? option.label);
            }) });
    };
    render_Review = () => {
        const stampedNote = ModuleFE_Notes.stampedNote;
        const content = formatNoteAIText(stampedNote?.note?.aiText, stampedNote?.dispositionType, this.removeDisposition);
        return _jsx(FadeScrollWrapper, { className: 'c__advisor-notes__review', onCopy: () => ModuleFE_Notes.analytics.note.copy(), children: content });
    };
    render_Sent = () => {
        const stampedNote = ModuleFE_Notes.stampedNote;
        const template = this.state.notes.find(n => n._id === stampedNote?.note.templateId);
        let h2Content = `${template?.advisorAppearance?.title ?? template?.title ?? 'N/A'} was sent to Epic`;
        let h3Content = 'Entire note sent';
        if (exists(stampedNote?.dispositionType))
            h3Content += ', including the disposition';
        return _jsxs(LL_V_L, { className: 'c__advisor-notes__sent', children: [_jsx(ICONS.advisor_avn_check.component, {}), _jsx("h1", { children: "Sent to EHR" }), _jsx("h2", { children: h2Content }), _jsx("h3", { children: h3Content })] });
    };
}
export const Component_AdvisorNotes = (p) => _jsx(AwaitModules, { Component: () => _jsx(Component, { ...p }), modules: Component.modules, customLoader: _jsx(Component_AdvisorSL_Notes, {}) });
//# sourceMappingURL=Component_AdvisorNotes.js.map